import { Color, DateUtil, Logger, LogManager } from "@mcleod/core";
import { DataSourceAction, DataSourceExecutionEvent, DropdownItem } from "@mcleod/components";
import { AutogenLayoutUserGeneral } from "./autogen/AutogenLayoutUserGeneral";
import { ColorSelectorUtil } from "../ColorSelectorUtil";

export class UserGeneral extends AutogenLayoutUserGeneral {
    private static log: Logger;
    private _evenRowColor: Color;
    private _oddRowColor: Color;

    override onLoad() {
        this.setupTimeFormats();
    }

    private setupTimeFormats() {
        const civilianFormat = new DropdownItem("C", "Civilian").setAddlValue("format", "hh:mma");
        const militaryFormat = new DropdownItem("M", "Military").setAddlValue("format", "HHmm");
        this.textTimeFormat.items = [ civilianFormat, militaryFormat ];
    }

    textDateFormatOnChange() {
        this.updateSampleDateTime();
    }

    textTimeFormatOnChange() {
        this.updateSampleDateTime();
    }

    private updateSampleDateTime() {
        const date = new Date();
        date.setFullYear(date.getFullYear(), 11, 31);
        date.setHours(16);
        date.setMinutes(35);
        let format = this.textDateFormat.selectedItem?.value ?? "MM/dd/yyyy";
        format += " ";
        format += this.textTimeFormat.selectedItem?.getAddlValue("format") ?? "hh:mma";
        this.labelSampleDateTime.text = DateUtil.formatDateTime(date, format);
    }

    async buttonEvenColorOnClick() {
        const selectedColor = await ColorSelectorUtil.promptForColor();
        UserGeneral.getLog().debug("Selected even row color: %o", selectedColor);
        this.evenRowColor = selectedColor;
    }

    async buttonOddColorOnClick() {
        const selectedColor = await ColorSelectorUtil.promptForColor();
        UserGeneral.getLog().debug("Selected odd row color: %o", selectedColor);
        this.oddRowColor = selectedColor;
    }

    buttonClearEvenColorOnClick() {
        this.evenRowColor = null;
    }

    buttonClearOddColorOnClick() {
        this.oddRowColor = null;
    }

    private get evenRowColor(): Color {
        return this._evenRowColor;
    }

    private set evenRowColor(value: Color) {
        if (value === this._evenRowColor) {
            return;
        }
        this._evenRowColor = value;
        this.sourceUserSettings.activeRow.set("table_even_row_color", this.evenRowColor);
        // Don't set color when in the designer...we don't want to serialize that color as part of the layout
        if (this._designer == null) {
            this.panelEvenColorDisplay.backgroundColor = this._evenRowColor ?? "";
        }
    }

    private get oddRowColor(): Color {
        return this._oddRowColor;
    }

    private set oddRowColor(value: Color) {
        if (value === this._oddRowColor) {
            return;
        }
        this._oddRowColor = value;
        this.sourceUserSettings.activeRow.set("table_odd_row_color", this.oddRowColor);
        // Don't set color when in the designer...we don't want to serialize that color as part of the layout
        if (this._designer == null) {
            this.panelOddColorDisplay.backgroundColor = this._oddRowColor ?? "";
        }
    }

    sourceUserSettingsAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.evenRowColor = this.sourceUserSettings.activeRow.get("table_even_row_color", undefined);
            this.oddRowColor = this.sourceUserSettings.activeRow.get("table_odd_row_color", undefined);
        }
    }

    private static getLog(): Logger {
        if (this.log == null) {
            this.log = LogManager.getLogger("common.user.LMEUserGeneral");
        }
        return this.log;
    }

    switchIsActiveOnChange() {
        if (this.sourceUserSettings.isAddingOrUpdating()) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (this.switchIsActive.checked) {
                if (this.textboxActiveDate.isEmpty()) {
                    this.sourceUserSettings.activeRow?.set("active_date", today);
                }
                this.sourceUserSettings.activeRow?.set("inactive_date", null);
            } else {
                if (this.textboxInactiveDate.isEmpty()) {
                    this.sourceUserSettings.activeRow?.set("inactive_date", today);
                }
            }
        }
    }
}
